import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { WeekDetail } from "./WeekDetail";
import { WeekPicker } from "./WeekPicker";
import { formatWeekRange } from "./utils";

export function ReleaseNotesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const weeksQuery = useQuery({
    queryKey: ["release-note-weeks"] as const,
    queryFn: () => api.getReleaseNoteWeeks(),
  });

  const weeks = useMemo(
    () => weeksQuery.data?.weeks ?? [],
    [weeksQuery.data?.weeks],
  );

  // URL param is the source of truth for the selected week. Default to the
  // most recent week once the weeks list resolves.
  const urlIsoWeek = searchParams.get("week");
  const selectedWeek = useMemo(() => {
    if (urlIsoWeek) {
      const hit = weeks.find((w) => w.isoWeek === urlIsoWeek);
      if (hit) return hit;
    }
    return weeks[0] ?? null;
  }, [urlIsoWeek, weeks]);

  const selectedIsoWeek = selectedWeek?.isoWeek ?? null;
  const selectedRange = selectedWeek
    ? { startDate: selectedWeek.rangeStart, endDate: selectedWeek.rangeEnd }
    : null;

  const entriesQueryKey = useMemo(
    () =>
      [
        "release-notes",
        selectedRange?.startDate ?? null,
        selectedRange?.endDate ?? null,
      ] as const,
    [selectedRange?.startDate, selectedRange?.endDate],
  );

  const entriesQuery = useQuery({
    queryKey: entriesQueryKey,
    queryFn: () => api.getReleaseNotesByDateRange(selectedRange!),
    enabled: !!selectedRange,
  });

  const handleSelectWeek = (isoWeek: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("week", isoWeek);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Release Notes"
        subtitle="Edit items shown on the public release-notes page"
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[18rem_1fr]">
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <WeekPicker
            weeks={weeks}
            selectedIsoWeek={selectedIsoWeek}
            onSelect={handleSelectWeek}
            isLoading={weeksQuery.isLoading}
          />
          {weeksQuery.isError && (
            <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
              {weeksQuery.error?.message || "Failed to load weeks"}
            </div>
          )}
        </aside>

        <main className="min-w-0">
          {selectedWeek && (
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">
                Week {selectedWeek.isoWeek.split("-W")[1]}:{" "}
                {formatWeekRange(selectedWeek.rangeStart, selectedWeek.rangeEnd)}
              </h2>
              <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
                {selectedWeek.releaseCount}{" "}
                {selectedWeek.releaseCount === 1 ? "release" : "releases"} this
                week &middot; {selectedWeek.userFacingItemCount} of{" "}
                {selectedWeek.totalItemCount} items are public
              </p>
            </div>
          )}

          {entriesQuery.isError && (
            <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
              {entriesQuery.error?.message || "Failed to load release notes"}
            </div>
          )}

          {selectedRange ? (
            <WeekDetail
              entries={entriesQuery.data?.entries ?? []}
              isLoading={entriesQuery.isLoading}
              queryKey={entriesQueryKey}
            />
          ) : (
            !weeksQuery.isLoading && (
              <div className="rounded-xl border border-dashed border-zinc-300 p-8 text-center text-sm text-zinc-500 dark:border-zinc-700 dark:text-zinc-400">
                Select a week from the left to edit its release notes.
              </div>
            )
          )}
        </main>
      </div>
    </div>
  );
}
